import { supabase } from '@/lib/supabase';
import { getKoreanDate } from '@/lib/dateUtils';

export interface ProductOffer {
  id: string;
  product_id: string;
  offer_type: 'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT' | 'REGULAR_ORDER';
  discount_rate: number;
  discounted_price: number;
  min_discount_rate?: number;
  is_random_set: boolean;
  pickup_start_time?: string;
  pickup_end_time?: string;
  reservation_available_days: number;
  max_quantity_per_order: number | null;
  is_active: boolean;
  // Offer lifecycle fields
  valid_from?: string;
  valid_until?: string;
  version?: number;
  is_current?: boolean;
  // TIME-based reservation discount fields
  reservation_available_start_time?: string; // TIME type - typically '00:00'
  reservation_available_end_time?: string;   // TIME type - typically closing time - 1 hour
  reservation_pickup_start_time?: string;    // TIME type - next day opening + 1 hour
  reservation_pickup_end_time?: string;      // TIME type - next day closing time
  // TIME-based deadline discount fields  
  deadline_pickup_start_time?: string;       // TIME type - pickup window start
  deadline_pickup_end_time?: string;         // TIME type - pickup window end
  deadline_order_start_time?: string;        // TIME type - order window start (pickup - 30min)
  deadline_order_end_time?: string;          // TIME type - order window end (pickup end)
}

export interface ProductStock {
  id: string;
  product_id: string;
  date: string;
  initial_stock: number;
  current_stock: number;
  sold_stock: number;
  version: number;
}

export interface Product {
  id: string;
  restaurant_id: string;
  name: string;
  description?: string;
  category?: string;
  original_price: number;
  image_url?: string;
  ingredients?: string[];
  allergens?: string[];
  calories?: number;
  is_available: boolean;
  offers?: ProductOffer[];
  stock?: ProductStock[];
  restaurant?: {
    id: string;
    name: string;
    image_url?: string;
  };
}

class ProductService {

  async getProductsByRestaurant(
    restaurantId: string, 
    offerType?: string
  ): Promise<Product[]> {
    try {
      let query = supabase
        .from('products')
        .select(`
          *,
          offers:product_offers!inner(*),
          stock:product_stock(*)
        `)
        .eq('restaurant_id', restaurantId)
        .eq('is_available', true)
        .eq('offers.is_active', true);
      
      // Only get active offers (simplified for compatibility)
      // Note: Will be enhanced with lifecycle fields after migration

      if (offerType) {
        query = query.eq('offers.offer_type', offerType);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching products:', error);
        return [];
      }

      // 각 제품의 오늘 날짜 재고 데이터만 선택
      const todayKST = getKoreanDate();
      const processedData = (data || []).map(product => {
        // 오늘 날짜의 재고만 선택
        const todayStock = product.stock?.find((s: any) => s.date === todayKST);
        
        return {
          ...product,
          stock: todayStock ? [todayStock] : []
        };
      });

      return processedData;
    } catch (error) {
      console.error('Error fetching products:', error);
      return [];
    }
  }

  async getProductById(productId: string): Promise<Product | null> {
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          offers:product_offers!inner(*),
          stock:product_stock(*),
          restaurant:restaurants(id, name, image_url)
        `)
        .eq('id', productId)
        .eq('offers.is_active', true)
        .single();

      if (error || !data) {
        console.error('Error fetching product:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error fetching product:', error);
      return null;
    }
  }

  async getAvailableOffers(productId: string, _date?: string): Promise<ProductOffer[]> {
    try {
      const { data, error } = await supabase
        .from('product_offers')
        .select('*')
        .eq('product_id', productId)
        .eq('is_active', true);

      if (error) {
        console.error('Error fetching offers:', error);
        return [];
      }

      // Filter offers based on availability rules
      const currentTime = new Date().toTimeString().slice(0, 5);
      
      return (data || []).filter(offer => {
        switch (offer.offer_type) {
          case 'DEADLINE_DISCOUNT':
            // Available from start time until closing
            return offer.pickup_start_time && currentTime >= offer.pickup_start_time;
          
          case 'RESERVATION_DISCOUNT':
            // Available for tomorrow orders until closing time
            return true; // Will be validated further in order creation
          
          case 'REGULAR_ORDER':
            // Always available during business hours
            return true;
          
          default:
            return false;
        }
      });
    } catch (error) {
      console.error('Error fetching available offers:', error);
      return [];
    }
  }

  async checkStockAvailability(
    productId: string, 
    quantity: number, 
    date?: string
  ): Promise<{ available: boolean; currentStock: number }> {
    try {
      const targetDate = date || new Date().toISOString().split('T')[0];
      
      const { data, error } = await supabase
        .from('product_stock')
        .select('current_stock')
        .eq('product_id', productId)
        .eq('date', targetDate)
        .single();

      if (error || !data) {
        return { available: false, currentStock: 0 };
      }

      return {
        available: data.current_stock >= quantity,
        currentStock: data.current_stock
      };
    } catch (error) {
      console.error('Error checking stock availability:', error);
      return { available: false, currentStock: 0 };
    }
  }

  async updateStock(
    productId: string, 
    newStock: number, 
    date?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const targetDate = date || new Date().toISOString().split('T')[0];
      
      const { error } = await supabase
        .from('product_stock')
        .upsert({
          product_id: productId,
          date: targetDate,
          initial_stock: newStock,
          current_stock: newStock,
          sold_stock: 0
        }, {
          onConflict: 'product_id,date'
        });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error updating stock:', error);
      return { success: false, error: '재고 업데이트에 실패했습니다.' };
    }
  }

  async createProductOffer(
    productId: string,
    offerData: Partial<ProductOffer>
  ): Promise<{ success: boolean; error?: string; data?: ProductOffer }> {
    try {
      // Validate discount rates based on offer type
      if (offerData.offer_type === 'DEADLINE_DISCOUNT') {
        const minRate = offerData.is_random_set ? 50 : 40;
        if ((offerData.discount_rate || 0) < minRate) {
          return { 
            success: false, 
            error: `마감할인은 ${offerData.is_random_set ? '랜덤세트 50%' : '개별상품 40%'} 이상 할인해야 합니다.` 
          };
        }
      } else if (offerData.offer_type === 'RESERVATION_DISCOUNT') {
        if ((offerData.discount_rate || 0) > 10) {
          return { success: false, error: '예약할인은 최대 10%까지 가능합니다.' };
        }
      }

      // Use the new version creation function for better lifecycle management
      const additionalData = {
        is_random_set: offerData.is_random_set || false,
        max_quantity_per_order: offerData.max_quantity_per_order || null,
        pickup_start_time: offerData.pickup_start_time,
        pickup_end_time: offerData.pickup_end_time,
        // TIME-based reservation discount fields
        reservation_available_start_time: offerData.reservation_available_start_time || '00:00',
        reservation_available_end_time: offerData.reservation_available_end_time,
        reservation_pickup_start_time: offerData.reservation_pickup_start_time,
        reservation_pickup_end_time: offerData.reservation_pickup_end_time,
        // TIME-based deadline discount fields
        deadline_pickup_start_time: offerData.deadline_pickup_start_time,
        deadline_pickup_end_time: offerData.deadline_pickup_end_time,
        deadline_order_start_time: offerData.deadline_order_start_time,
        deadline_order_end_time: offerData.deadline_order_end_time
      };

      const { data: versionResult, error } = await supabase
        .rpc('create_new_offer_version', {
          p_product_id: productId,
          p_offer_type: offerData.offer_type,
          p_discount_rate: offerData.discount_rate || 0,
          p_discounted_price: offerData.discounted_price || 0,
          p_additional_data: additionalData
        })
        .single();

      if (error || !versionResult || !(versionResult as any)?.success) {
        return { success: false, error: (versionResult as any)?.message || error?.message };
      }

      // Get the newly created offer
      const { data: newOffer, error: fetchError } = await supabase
        .from('product_offers')
        .select('*')
        .eq('id', (versionResult as any).new_offer_id)
        .single();

      if (fetchError) {
        return { success: false, error: fetchError.message };
      }

      return { success: true, data: newOffer };
    } catch (error) {
      console.error('Error creating product offer:', error);
      return { success: false, error: '상품 제안 생성에 실패했습니다.' };
    }
  }

  async updateProductOffer(
    offerId: string,
    updates: Partial<ProductOffer>
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Get current offer data
      const { data: currentOffer, error: fetchError } = await supabase
        .from('product_offers')
        .select('*')
        .eq('id', offerId)
        .single();

      if (fetchError || !currentOffer) {
        return { success: false, error: '오퍼를 찾을 수 없습니다.' };
      }

      // Check if this is a significant change that requires new version
      const shouldCreateNewVersion = await supabase
        .rpc('should_create_new_offer_version', {
          p_offer_id: offerId,
          p_new_discount_rate: updates.discount_rate !== undefined ? updates.discount_rate : currentOffer.discount_rate,
          p_new_discounted_price: updates.discounted_price !== undefined ? updates.discounted_price : currentOffer.discounted_price,
          p_additional_changes: updates
        })
        .single();

      if ((shouldCreateNewVersion.data as any)) {
        // Create new version for significant changes
        const additionalData = {
          is_random_set: updates.is_random_set !== undefined ? updates.is_random_set : currentOffer.is_random_set,
          max_quantity_per_order: updates.max_quantity_per_order !== undefined ? updates.max_quantity_per_order : currentOffer.max_quantity_per_order,
          pickup_start_time: updates.pickup_start_time || currentOffer.pickup_start_time,
          pickup_end_time: updates.pickup_end_time || currentOffer.pickup_end_time,
        };

        const versionResult = await supabase
          .rpc('create_new_offer_version', {
            p_product_id: currentOffer.product_id,
            p_offer_type: updates.offer_type || currentOffer.offer_type,
            p_discount_rate: updates.discount_rate !== undefined ? updates.discount_rate : currentOffer.discount_rate,
            p_discounted_price: updates.discounted_price !== undefined ? updates.discounted_price : currentOffer.discounted_price,
            p_additional_data: additionalData
          })
          .single();

        if (!(versionResult.data as any)?.success) {
          return { success: false, error: (versionResult.data as any)?.message || '새 버전 생성에 실패했습니다.' };
        }
      } else {
        // Minor update - just update the existing offer
        const { error } = await supabase
          .from('product_offers')
          .update({
            ...updates,
            updated_at: new Date().toISOString()
          })
          .eq('id', offerId);

        if (error) {
          return { success: false, error: error.message };
        }
      }

      return { success: true };
    } catch (error) {
      console.error('Error updating product offer:', error);
      return { success: false, error: '상품 제안 업데이트에 실패했습니다.' };
    }
  }

  async getProductStockHistory(
    productId: string,
    startDate?: string,
    endDate?: string
  ): Promise<ProductStock[]> {
    try {
      let query = supabase
        .from('product_stock')
        .select('*')
        .eq('product_id', productId)
        .order('date', { ascending: false });

      if (startDate) {
        query = query.gte('date', startDate);
      }
      if (endDate) {
        query = query.lte('date', endDate);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching stock history:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching stock history:', error);
      return [];
    }
  }

  async getDeadlineDiscountProducts(
    _userLocation?: { lat: number; lng: number },
    _maxDistance?: number
  ): Promise<Product[]> {
    try {
      let query = supabase
        .from('products')
        .select(`
          *,
          offers:product_offers!inner(*),
          stock:product_stock(*),
          restaurant:restaurants(*)
        `)
        .eq('is_available', true)
        .eq('offers.offer_type', 'DEADLINE_DISCOUNT')
        .eq('offers.is_active', true);

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching deadline discount products:', error);
        return [];
      }

      // 각 제품의 오늘 날짜 재고 데이터만 선택
      const todayKST = getKoreanDate();
      const processedData = (data || []).map(product => {
        // 오늘 날짜의 재고만 선택
        const todayStock = product.stock?.find((s: any) => s.date === todayKST);
        
        return {
          ...product,
          stock: todayStock ? [todayStock] : []
        };
      });

      // TODO: Add distance filtering if userLocation is provided
      return processedData;
    } catch (error) {
      console.error('Error fetching deadline discount products:', error);
      return [];
    }
  }

  async searchProducts(
    searchTerm: string,
    filters?: {
      category?: string;
      maxPrice?: number;
      offerType?: string;
      restaurantId?: string;
    }
  ): Promise<Product[]> {
    try {
      let query = supabase
        .from('products')
        .select(`
          *,
          offers:product_offers!inner(*),
          stock:product_stock(*),
          restaurant:restaurants(id, name, image_url)
        `)
        .eq('is_available', true)
        .eq('offers.is_active', true)
        .ilike('name', `%${searchTerm}%`);

      if (filters?.category) {
        query = query.eq('category', filters.category);
      }
      if (filters?.maxPrice) {
        query = query.lte('original_price', filters.maxPrice);
      }
      if (filters?.offerType) {
        query = query.eq('offers.offer_type', filters.offerType);
      }
      if (filters?.restaurantId) {
        query = query.eq('restaurant_id', filters.restaurantId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error searching products:', error);
        return [];
      }

      // Process data to get today's stock for each product
      const todayKST = getKoreanDate();
      const processedData = (data || []).map(product => {
        // Get only today's stock
        const todayStock = product.stock?.find((s: any) => s.date === todayKST);
        
        return {
          ...product,
          stock: todayStock ? [todayStock] : []
        };
      });

      return processedData;
    } catch (error) {
      console.error('Error searching products:', error);
      return [];
    }
  }
}

export const productService = new ProductService();